import DmsSetting from "entities/DmsSetting";
import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/configureStore";
import { updateDmsSettings } from "store/slices/settings";

interface DmsSettingsModalProps {
    show: boolean;
    onHide: () => void;
    dmsSettings: DmsSetting | null;
    tenantId: number;
}

const DmsSettingsModal: React.FC<DmsSettingsModalProps> = ({ show, onHide, dmsSettings, tenantId }) => {
    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();
    const defaultState: DmsSetting = {
        AzureAccountName: "",
        IncomingBlobContainer: "",
        SafeBlobContainer: "",
        DmsUrl: "",
        DmsClientId: "",
        JobDescriptionDocumentType: "",
        DmsTenantId: "",
        DmsScope: "",
    }
    const [validated, setValidated] = useState(false);
    const [formData, setFormData] = useState<DmsSetting>(defaultState);

    useEffect(() => {
        if (show) {
            if (dmsSettings) {
                setFormData({
                    AzureAccountName: dmsSettings.AzureAccountName || "",
                    IncomingBlobContainer: dmsSettings.IncomingBlobContainer || "",
                    SafeBlobContainer: dmsSettings.SafeBlobContainer || "",
                    DmsUrl: dmsSettings.DmsUrl || "",
                    DmsClientId: dmsSettings.DmsClientId || "",
                    JobDescriptionDocumentType: dmsSettings.JobDescriptionDocumentType || "",
                    DmsTenantId: dmsSettings.DmsTenantId || "",
                    DmsScope: dmsSettings.DmsScope || ""
                });
            } else {
                setFormData(defaultState);
            }
            setValidated(false);
        }
    }, [show, dmsSettings]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        const form = e.currentTarget;
        e.preventDefault();
        e.stopPropagation();
        if (form.checkValidity() === false) {
            setValidated(true);
        } else {
            dispatch(updateDmsSettings(tenantId, formData));
            onHide();
        }
    }

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    DMS {t("settings.paySettings.parameters")}
                </Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3" controlId="formAzureAccountName">
                        <Form.Label>{t(`settings.dmsParameters.AzureAccountName`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="AzureAccountName"
                            value={formData.AzureAccountName}
                            onChange={handleChange}
                            maxLength={100}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formIncomingBlobContainer">
                        <Form.Label>{t(`settings.dmsParameters.IncomingBlobContainer`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="IncomingBlobContainer"
                            value={formData.IncomingBlobContainer}
                            onChange={handleChange}
                            maxLength={50}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formSafeBlobContainer">
                        <Form.Label>{t(`settings.dmsParameters.SafeBlobContainer`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="SafeBlobContainer"
                            value={formData.SafeBlobContainer}
                            onChange={handleChange}
                            maxLength={50}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formJobDescriptionDocumentType">
                        <Form.Label>{t(`settings.dmsParameters.JobDescriptionDocumentType`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="JobDescriptionDocumentType"
                            value={formData.JobDescriptionDocumentType}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formDmsUrl">
                        <Form.Label>{t(`settings.dmsParameters.DmsUrl`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="DmsUrl"
                            value={formData.DmsUrl}
                            onChange={handleChange}
                            maxLength={100}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formDmsClientId">
                        <Form.Label>{t(`settings.dmsParameters.DmsClientId`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="DmsClientId"
                            value={formData.DmsClientId}
                            onChange={handleChange}
                            maxLength={36}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formDmsTenantId">
                        <Form.Label>{t(`settings.dmsParameters.DmsTenantId`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="DmsTenantId"
                            value={formData.DmsTenantId}
                            onChange={handleChange}
                            maxLength={36}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formDmsScope">
                        <Form.Label>{t(`settings.dmsParameters.DmsScope`)}</Form.Label>
                        <Form.Control
                            type="text"
                            name="DmsScope"
                            value={formData.DmsScope}
                            onChange={handleChange}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" type="button" onClick={onHide}>
                        {t("common:form.buttons.cancel")}
                    </Button>
                    <Button variant="primary" type="submit">
                           {t("common:form.buttons.save")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DmsSettingsModal;